import React, { createContext, useContext, useEffect, useState } from 'react';
import { BrandingConfig } from '../types/branding';
import { whitelabelConfig } from '../config/whitelabel.config';

interface BrandingContextType {
  branding: BrandingConfig;
  setActiveOrganization: (orgId: string) => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

interface BrandingProviderProps {
  children: React.ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [activeOrganization, setActiveOrganization] = useState(whitelabelConfig.activeOrganization);
  const [branding, setBranding] = useState<BrandingConfig>(
    whitelabelConfig.organizations[whitelabelConfig.activeOrganization]
  );

  useEffect(() => {
    const newBranding = whitelabelConfig.organizations[activeOrganization];
    if (!newBranding) {
      console.error(`Organization ${activeOrganization} not found in config`);
      return;
    }

    setBranding(newBranding);

    // Apply only branding-specific CSS variables
    const root = document.documentElement;
    
    // Typography
    root.style.setProperty('--font-family', newBranding.typography.fontFamily);
    if (newBranding.typography.headingFontFamily) {
      root.style.setProperty('--heading-font-family', newBranding.typography.headingFontFamily);
    }
    if (newBranding.typography.majorFontFamily) {
      root.style.setProperty('--major-font-family', newBranding.typography.majorFontFamily);
    }

    // Font sizes
    Object.entries(newBranding.typography.fontSize).forEach(([key, value]) => {
      root.style.setProperty(`--font-size-${key}`, value);
    });

    // Border radius
    if (newBranding.borderRadius) {
      root.style.setProperty('--border-radius', newBranding.borderRadius);
    }

    // Spacing
    if (newBranding.spacing) {
      Object.entries(newBranding.spacing).forEach(([key, value]) => {
        root.style.setProperty(`--spacing-${key}`, value);
      });
    }
  }, [activeOrganization]);

  return (
    <BrandingContext.Provider value={{ branding, setActiveOrganization }}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext; 